// Cotizaciones leídas directo de SAP. Vendedor: solo las suyas. Admin: todas o por vendedor.
// Los filtros se mandan a SAP ($filter); aquí solo se capturan.
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Card } from '@/components/ui/Card'
import { PageHeader } from '@/components/ui/PageHeader'
import { Badge } from '@/components/ui/Badge'
import { Input, Select } from '@/components/ui/Field'
import { useAuth } from '@/hooks/useAuth'
import { formatMoney } from '@/services/deliveries'
import { getQuoteMeta, listQuotes, listSellers, type QuoteFilters } from '@/services/quotes'

function useDebounced(value: string, ms = 400) {
  const [v, setV] = useState(value)
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t) }, [value, ms])
  return v
}

const EMPTY: QuoteFilters = { seller: '', q: '', date_from: '', date_to: '', status: '' }

export default function QuoteList() {
  const { user } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [f, setF] = useState<QuoteFilters>(EMPTY)
  const q = useDebounced(f.q ?? '')
  const filters = { ...f, q }

  const meta = useQuery({ queryKey: ['quotes', 'meta'], queryFn: getQuoteMeta, staleTime: 10 * 60_000 })
  const sellers = useQuery({ queryKey: ['quotes', 'sellers'], queryFn: listSellers, enabled: isAdmin, staleTime: 60 * 60_000 })
  const { data, isLoading, isFetching, error } = useQuery({ queryKey: ['quotes', 'list', filters], queryFn: () => listQuotes(filters) })

  const set = (ch: Partial<QuoteFilters>) => setF((prev) => ({ ...prev, ...ch }))
  const dirty = Object.values(filters).some((v) => v !== '' && v != null)

  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader
        title="Cotizaciones"
        subtitle={
          meta.data?.sales_person
            ? `Vendedor SAP: ${meta.data.sales_person.name}`
            : meta.data && !isAdmin
              ? 'Tu correo no corresponde a ningún vendedor de SAP; pide a administración que lo corrija.'
              : meta.data ? 'Vista de administrador: todas las cotizaciones de SAP.' : undefined
        }
        actions={<Link to="/cotizaciones/nueva"><Button>＋ Nueva cotización</Button></Link>}
      />

      {/* Filtros */}
      <Card className="mb-4 p-3">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
          <div className="col-span-2">
            <Input label="Buscar" placeholder="Folio, cliente u observaciones" value={f.q ?? ''} onChange={(e) => set({ q: e.target.value })} />
          </div>
          {isAdmin && (
            <div className="col-span-2 sm:col-span-1">
              <Select label="Vendedor" value={f.seller ?? ''} onChange={(e) => set({ seller: e.target.value === '' ? '' : Number(e.target.value) })}>
                <option value="">Todos</option>
                {sellers.data?.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
              </Select>
            </div>
          )}
          <Input label="Desde" type="date" value={f.date_from ?? ''} onChange={(e) => set({ date_from: e.target.value })} />
          <Input label="Hasta" type="date" value={f.date_to ?? ''} onChange={(e) => set({ date_to: e.target.value })} />
          <Select label="Estado" value={f.status ?? ''} onChange={(e) => set({ status: e.target.value as QuoteFilters['status'] })}>
            <option value="">Todas</option>
            <option value="bost_Open">Abiertas</option>
            <option value="bost_Close">Cerradas</option>
          </Select>
        </div>
        <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
          <span>{isFetching ? 'Consultando SAP…' : data ? `${data.length} resultado${data.length === 1 ? '' : 's'} (máx. 50, las más recientes)` : ''}</span>
          {dirty && <button className="font-medium text-ds-700 hover:underline" onClick={() => setF(EMPTY)}>Limpiar filtros</button>}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-200 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="p-3">Folio</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Cliente</th>
                {isAdmin && <th className="p-3">Vendedor</th>}
                <th className="p-3 text-right">Total</th>
                <th className="p-3">Vigencia</th>
                <th className="p-3">Estado</th>
                <th className="p-3 text-right">PDF</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading && <tr><td colSpan={8} className="p-4 text-center text-gray-400">Consultando SAP…</td></tr>}
              {error && <tr><td colSpan={8} className="p-4 text-center text-red-600">{(error as { message?: string }).message ?? 'Error'}</td></tr>}
              {data?.map((qt) => (
                <tr key={qt.DocEntry} className="hover:bg-gray-50/60">
                  <td className="p-3 font-mono font-semibold text-gray-900">{qt.DocNum}</td>
                  <td className="p-3 text-gray-600">{qt.DocDate}</td>
                  <td className="p-3 text-gray-800">
                    <div className="font-medium">{qt.CardName ?? qt.CardCode}</div>
                    {qt.Comments && <div className="text-xs text-gray-400">{qt.Comments}</div>}
                  </td>
                  {isAdmin && (
                    <td className="p-3 text-xs text-gray-600">
                      {sellers.data?.find((s) => s.code === qt.SalesPersonCode)?.name ?? <span className="text-gray-400">sin vendedor</span>}
                    </td>
                  )}
                  <td className="p-3 text-right font-semibold tabular-nums">{formatMoney(qt.TotalDoc, qt.Currency)}</td>
                  <td className="p-3 text-gray-600">{qt.DocDueDate}</td>
                  <td className="p-3">
                    <Badge variant={qt.DocumentStatus === 'bost_Open' ? 'green' : 'gray'}>
                      {qt.DocumentStatus === 'bost_Open' ? 'Abierta' : 'Cerrada'}
                    </Badge>
                  </td>
                  <td className="p-3 text-right">
                    {/* Liga normal (no window.open): en la app Android la abre el visor del sistema. */}
                    <a href={qt.pdf_url} target="_blank" rel="noopener" className="text-sm font-medium text-ds-700 hover:underline">
                      Abrir PDF
                    </a>
                  </td>
                </tr>
              ))}
              {data && data.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-gray-400">Sin cotizaciones con esos filtros.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  )
}
